import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Button } from '../common/Catalyst/button';
import { ReservationPolicyReturn } from '../clients/rust-api-client';
import { reservationService } from '../clients/ApiClients';
import {
  WEEKDAYS,
  isValidFee,
  optionalInt,
  reservationErrorMessage,
} from '../reservation/reservationMeta';
import {
  checkboxClass,
  helpClass,
  inputClass,
  labelClass,
  sectionTitleClass,
} from '../reservation/formStyles';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import { FieldPicker } from '../common/Custom/FieldPicker';

/**
 * The association's reservation policy (#642) — Owner/Admin only.
 *
 * The good-standing gate is the reason this is not an amenity setting:
 * whether an association may suspend amenity use for an unpaid balance is
 * a legal question that varies by state (Florida allows it past 90 days,
 * California requires due process first), so it is **off** until an
 * Owner or Admin turns it on deliberately, and each amenity then opts in.
 */
export const ReservationPolicyPanel: React.FC<{ hoaId: string }> = ({ hoaId }) => {
  const [policy, setPolicy] = useState<ReservationPolicyReturn | null>(null);
  const [gate, setGate] = useState(false);
  const [maxDays, setMaxDays] = useState('');
  const [maxBalance, setMaxBalance] = useState('');
  const [expiry, setExpiry] = useState('72');
  const [reminder, setReminder] = useState('24');
  const [weekStart, setWeekStart] = useState(0);
  // Phase 3: deposits, the waitlist, no-show suspensions and check-in.
  const [releaseDays, setReleaseDays] = useState('7');
  const [offerHours, setOfferHours] = useState('12');
  const [noShowThreshold, setNoShowThreshold] = useState('');
  const [noShowWindow, setNoShowWindow] = useState('90');
  const [suspensionDays, setSuspensionDays] = useState('30');
  const [grace, setGrace] = useState('60');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = (p: ReservationPolicyReturn) => {
    setPolicy(p);
    setGate(p.standing_gate_enabled);
    setMaxDays(p.standing_max_days_past_due?.toString() ?? '');
    setMaxBalance(p.standing_max_balance ?? '');
    setExpiry(String(p.pending_expiry_hours));
    setReminder(String(p.reminder_hours_before));
    setWeekStart(p.week_starts_on);
    setReleaseDays(p.deposit_auto_release_days?.toString() ?? '');
    setOfferHours(String(p.waitlist_offer_hours));
    setNoShowThreshold(p.no_show_threshold?.toString() ?? '');
    setNoShowWindow(String(p.no_show_window_days));
    setSuspensionDays(String(p.suspension_days));
    setGrace(String(p.check_in_grace_minutes));
  };

  useEffect(() => {
    let live = true;
    reservationService
      .getReservationPolicy({ hoaId })
      .then((r) => live && load(r.data))
      .catch((err) => live && setError(reservationErrorMessage(err, "Couldn't load the reservation policy.")));
    return () => {
      live = false;
    };
  }, [hoaId]);

  const days = optionalInt(maxDays);
  const expiryHours = optionalInt(expiry);
  const reminderHours = optionalInt(reminder);
  const balanceOk = maxBalance.trim() === '' || isValidFee(maxBalance);
  const p3 = {
    releaseDays: optionalInt(releaseDays),
    offerHours: optionalInt(offerHours),
    noShowThreshold: optionalInt(noShowThreshold),
    noShowWindow: optionalInt(noShowWindow),
    suspensionDays: optionalInt(suspensionDays),
    grace: optionalInt(grace),
  };
  const required = (n: number | null) => n !== null && !Number.isNaN(n);
  const p3Ok =
    !Number.isNaN(p3.releaseDays) &&
    !Number.isNaN(p3.noShowThreshold) &&
    required(p3.offerHours) &&
    p3.offerHours! > 0 &&
    required(p3.noShowWindow) &&
    p3.noShowWindow! > 0 &&
    required(p3.suspensionDays) &&
    p3.suspensionDays! > 0 &&
    required(p3.grace);
  const valid =
    p3Ok &&
    !Number.isNaN(days) &&
    balanceOk &&
    expiryHours !== null &&
    !Number.isNaN(expiryHours) &&
    expiryHours > 0 &&
    reminderHours !== null &&
    !Number.isNaN(reminderHours);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const res = await reservationService.updateReservationPolicy({
        hoaId,
        reservationPolicyUpdate: {
          standing_gate_enabled: gate,
          standing_max_days_past_due: days,
          standing_max_balance: maxBalance.trim() || null,
          pending_expiry_hours: expiryHours!,
          reminder_hours_before: reminderHours!,
          week_starts_on: weekStart,
          deposit_auto_release_days: p3.releaseDays,
          waitlist_offer_hours: p3.offerHours!,
          no_show_threshold: p3.noShowThreshold && p3.noShowThreshold > 0 ? p3.noShowThreshold : null,
          no_show_window_days: p3.noShowWindow!,
          suspension_days: p3.suspensionDays!,
          check_in_grace_minutes: p3.grace!,
        },
      });
      load(res.data);
      setSaved(true);
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't save the reservation policy."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-4 rounded-lg border border-line px-4 py-4">
      <div>
        <h2 className={sectionTitleClass}>Reservation policy</h2>
        <p className={helpClass}>
          Applies to every amenity in this association. Times in {policy?.timezone ?? '…'}.
        </p>
      </div>

      <div className="space-y-2">
        <label className="flex items-start gap-2 text-[13px] text-fg-secondary">
          <input
            type="checkbox"
            className={clsx(checkboxClass, 'mt-0.5')}
            checked={gate}
            onChange={(e) => setGate(e.target.checked)}
          />
          <span>
            Block bookings from homes that are behind on their account
            <span className={clsx(helpClass, 'block')}>
              Off by default. Whether an association may suspend amenity use for an unpaid balance
              depends on state law and your governing documents — check before turning this on. Each
              amenity also has to require good standing for it to apply. Bookings already approved
              are not affected.
            </span>
          </span>
        </label>
        {gate && (
          <div className="ml-6 flex flex-wrap items-end gap-3">
            <p className={clsx(helpClass, 'w-full')}>
              A home is blocked when it passes either threshold. Leave both empty to block any home
              with an overdue balance.
            </p>
            <label className={labelClass}>
              More than this many days past due
              <input
                inputMode="numeric"
                value={maxDays}
                onChange={(e) => setMaxDays(e.target.value.replace(/\D/g, ''))}
                className={clsx(inputClass, 'w-28', Number.isNaN(days) && 'border-danger')}
              />
            </label>
            <label className={labelClass}>
              Or a balance over
              <span className={clsx(inputClass, 'flex w-32 items-center', !balanceOk && 'border-danger')}>
                <span className="text-fg-muted">$</span>
                <input
                  inputMode="decimal"
                  value={maxBalance}
                  onChange={(e) => setMaxBalance(e.target.value.replace(/[^\d.]/g, ''))}
                  className="w-full border-0 bg-transparent p-0 pl-1 text-[13px] text-fg focus:outline-none focus:ring-0"
                />
              </span>
            </label>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <label className={labelClass}>
          Unanswered requests expire after (hours)
          <input
            inputMode="numeric"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value.replace(/\D/g, ''))}
            className={clsx(inputClass, 'w-28')}
          />
        </label>
        <label className={labelClass}>
          Remind households (hours before)
          <input
            inputMode="numeric"
            value={reminder}
            onChange={(e) => setReminder(e.target.value.replace(/\D/g, ''))}
            className={clsx(inputClass, 'w-28')}
          />
        </label>
        <div className={labelClass}>
          Weekly limits count from
          <FieldPicker
            size="sm"
            className="w-36"
            label="Weekly limits count from"
            value={String(weekStart)}
            onChange={(v) => setWeekStart(Number(v))}
            options={WEEKDAYS.map((d, i) => ({ value: String(i), label: d }))}
          />
        </div>
      </div>

      <div className="space-y-3 border-t border-line/60 pt-4">
        <div className="flex flex-wrap gap-3">
          <label className={labelClass}>
            Return held deposits after (days)
            <input
              inputMode="numeric"
              value={releaseDays}
              onChange={(e) => setReleaseDays(e.target.value.replace(/\D/g, ''))}
              placeholder="Never"
              className={clsx(inputClass, 'w-28')}
            />
            <span className={clsx(helpClass, 'font-normal')}>Empty: a manager settles every deposit.</span>
          </label>
          <label className={labelClass}>
            Waitlist offers held for (hours)
            <input
              inputMode="numeric"
              value={offerHours}
              onChange={(e) => setOfferHours(e.target.value.replace(/\D/g, ''))}
              className={clsx(inputClass, 'w-28')}
            />
          </label>
          <label className={labelClass}>
            Check-in opens (minutes before)
            <input
              inputMode="numeric"
              value={grace}
              onChange={(e) => setGrace(e.target.value.replace(/\D/g, ''))}
              className={clsx(inputClass, 'w-28')}
            />
          </label>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className={labelClass}>
            Suspend a home after this many no-shows
            <input
              inputMode="numeric"
              value={noShowThreshold}
              onChange={(e) => setNoShowThreshold(e.target.value.replace(/\D/g, ''))}
              placeholder="Off"
              className={clsx(inputClass, 'w-28')}
            />
          </label>
          <label className={labelClass}>
            Within (days)
            <input
              inputMode="numeric"
              value={noShowWindow}
              onChange={(e) => setNoShowWindow(e.target.value.replace(/\D/g, ''))}
              className={clsx(inputClass, 'w-24')}
            />
          </label>
          <label className={labelClass}>
            For (days)
            <input
              inputMode="numeric"
              value={suspensionDays}
              onChange={(e) => setSuspensionDays(e.target.value.replace(/\D/g, ''))}
              className={clsx(inputClass, 'w-24')}
            />
          </label>
          <p className={clsx(helpClass, 'w-full')}>
            Off by default. When on, a home that reaches the count is barred from booking every amenity for the
            period and told why; managers can lift it with a reason. Applied the same way to every home, board
            members included.
          </p>
        </div>
      </div>

      <ErrorAlert>{error}</ErrorAlert>
      <div className="flex items-center gap-3">
        <Button color="sky" type="submit" disabled={!valid || saving || !policy}>
          {saving ? 'Saving…' : 'Save policy'}
        </Button>
        {saved && <span className="text-[12px] text-fg-muted">Saved.</span>}
      </div>
    </form>
  );
};

export default ReservationPolicyPanel;
